import { useAuth } from "@dl/web-core-js/auth";
import { Button, Card, Field, useConfirm } from "@dl/web-core-js/ui";
import { ExternalLink, Trash2, Upload } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  APPROVED_USES,
  ASSEMBLY_LEVELS,
  BED_DESIGNS,
  COLOR_MAPS,
  DISTRESSING_TYPES,
  MATTRESS_SIZES,
  MOUNTING_TYPES,
  NATURAL_VARIATION_TYPES,
  ProductClient,
  SHAPES,
  SLAT_MATERIALS,
  type ProductAssemblyDataDTO,
  type ProductCertificationDataDTO,
  type ProductDTO,
  type ProductSupplementaryDataDTO,
} from "../../api/product";
import { SpecForm, type SpecField } from "./SpecForm";

interface TabProps {
  draft: ProductDTO;
  onChange: (next: ProductDTO) => void;
  readOnly: boolean;
  /** Refetch after an immediate write (the PDF uploads bypass the draft). */
  onPersisted: () => void;
}

/** Assembly requirements plus the two instruction PDFs. */
export function AssemblyTab({ draft, onChange, readOnly, onPersisted }: TabProps) {
  const data = draft.assembly_data ?? {};

  const fields: Array<SpecField<ProductAssemblyDataDTO>> = [
    { kind: "bool", key: "is_assembly_required", labelKey: "product.assembly.required", fallback: "Assembly required" },
    { kind: "bool", key: "is_box_spring_required", labelKey: "product.assembly.box-spring", fallback: "Box spring required" },
    { kind: "select", key: "assembly_level", labelKey: "product.assembly.level", fallback: "Assembly level", options: ASSEMBLY_LEVELS },
    { kind: "int", key: "estimated_assembly_time", labelKey: "product.assembly.time", fallback: "Estimated assembly time (min)" },
    { kind: "int", key: "max_tv_size_in_inches", labelKey: "product.assembly.max-tv", fallback: "Max TV size (in)" },
    { kind: "select", key: "mattress_size_compatibility", labelKey: "product.assembly.mattress", fallback: "Mattress size compatibility", options: MATTRESS_SIZES },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <InstructionsFile kind="assembly" draft={draft} readOnly={readOnly} onPersisted={onPersisted} />
          <InstructionsFile kind="care" draft={draft} readOnly={readOnly} onPersisted={onPersisted} />
        </div>
      </Card>
      <Card className="p-5">
        <SpecForm
          fields={fields}
          value={data as ProductAssemblyDataDTO & Record<string, unknown>}
          onChange={(next) => onChange({ ...draft, assembly_data: next })}
          readOnly={readOnly}
        />
      </Card>
    </div>
  );
}

/**
 * The assembly/care instructions PDF.
 *
 * Uploads and deletes persist immediately — the file lands in S3 the moment it
 * is chosen, exactly as the Vaadin form behaved, so this shows its own state
 * rather than riding the page draft.
 */
function InstructionsFile({
  kind,
  draft,
  readOnly,
  onPersisted,
}: {
  kind: "assembly" | "care";
  draft: ProductDTO;
  readOnly: boolean;
  onPersisted: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const client = useMemo(() => new ProductClient(http), [http]);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const stored =
    kind === "assembly" ? draft.assembly_data?.assembly_instructions : draft.assembly_data?.care_instructions;
  const label =
    kind === "assembly"
      ? t("product.assembly.instructions", "Assembly instructions")
      : t("product.assembly.care", "Care instructions");

  const upload = async (file: File) => {
    setBusy(true);
    try {
      await client.addInstructions(
        kind === "assembly" ? "assembly-instructions" : "care-instructions",
        draft.id,
        file,
      );
      onPersisted();
      toast.success(t("notification.saved", "Saved"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Failed"));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const remove = async () => {
    const confirmed = await confirm({
      title: t("product.assembly.delete.title", "Delete this document?"),
      message: label,
      variant: "danger",
    });
    if (!confirmed) return;
    setBusy(true);
    try {
      await client.deleteInstructions(kind, draft.id);
      onPersisted();
      toast.success(t("notification.deleted", "Deleted"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Field label={label} htmlFor={`file-${kind}`}>
      {stored?.url ? (
        <div className="flex items-center gap-2">
          <a
            href={stored.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[13px] text-accent hover:underline"
          >
            <ExternalLink className="size-3.5" />
            {stored.file_name ?? t("product.assembly.document", "Document")}
          </a>
          {!readOnly && (
            <Button
              size="sm"
              variant="ghost"
              className="size-7 p-0"
              aria-label={t("button.delete", "Delete")}
              disabled={busy}
              onClick={() => void remove()}
              icon={<Trash2 className="size-3.5" />}
            />
          )}
        </div>
      ) : readOnly ? (
        <p className="text-[13px] text-ink-faint"></p>
      ) : (
        <>
          <Button
            size="sm"
            variant="secondary"
            loading={busy}
            icon={<Upload className="size-4" />}
            onClick={() => input.current?.click()}
          >
            {t("product.assembly.upload", "Upload PDF")}
          </Button>
          <input
            ref={input}
            id={`file-${kind}`}
            type="file"
            accept="application/pdf"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
            }}
          />
        </>
      )}
    </Field>
  );
}

/** The ~30 optional spec-sheet attributes retailer feeds pull from. */
export function SupplementaryTab({ draft, onChange, readOnly }: Omit<TabProps, "onPersisted">) {
  const data = draft.supplementary_data ?? {};

  const fields: Array<SpecField<ProductSupplementaryDataDTO>> = [
    { kind: "bool", key: "is_product_set", labelKey: "product.supp.product-set", fallback: "Product set" },
    { kind: "bool", key: "made_to_order", labelKey: "product.supp.made-to-order", fallback: "Made to order" },
    { kind: "text", key: "material", labelKey: "product.supp.material", fallback: "Material" },
    { kind: "text", key: "finish", labelKey: "product.supp.finish", fallback: "Finish" },
    { kind: "text", key: "style", labelKey: "product.supp.style", fallback: "Style" },
    { kind: "text", key: "pattern", labelKey: "product.supp.pattern", fallback: "Pattern" },
    { kind: "select", key: "color_map", labelKey: "product.supp.color-map", fallback: "Color map", options: COLOR_MAPS },
    { kind: "text", key: "closure_type", labelKey: "product.supp.closure", fallback: "Closure type" },
    { kind: "text", key: "door_style", labelKey: "product.supp.door-style", fallback: "Door style" },
    { kind: "text", key: "headboard_design", labelKey: "product.supp.headboard-design", fallback: "Headboard design" },
    { kind: "text", key: "headboard_type", labelKey: "product.supp.headboard-type", fallback: "Headboard type" },
    { kind: "text", key: "frame_type", labelKey: "product.supp.frame-type", fallback: "Frame type" },
    { kind: "select", key: "shape", labelKey: "product.supp.shape", fallback: "Shape", options: SHAPES },
    { kind: "select", key: "mounting_type", labelKey: "product.supp.mounting", fallback: "Mounting type", options: MOUNTING_TYPES },
    { kind: "bool", key: "finished_back", labelKey: "product.supp.finished-back", fallback: "Finished back" },
    { kind: "bool", key: "center_support_legs", labelKey: "product.supp.center-legs", fallback: "Center support legs" },
    { kind: "select", key: "bed_design", labelKey: "product.supp.bed-design", fallback: "Bed design", options: BED_DESIGNS },
    { kind: "select", key: "purposeful_distressing_type", labelKey: "product.supp.distressing", fallback: "Purposeful distressing", options: DISTRESSING_TYPES },
    { kind: "select", key: "natural_variation_type", labelKey: "product.supp.natural-variation", fallback: "Natural variation", options: NATURAL_VARIATION_TYPES },
    { kind: "select", key: "slat_material", labelKey: "product.supp.slat-material", fallback: "Slat material", options: SLAT_MATERIALS },
    { kind: "text", key: "upholstery_material", labelKey: "product.supp.upholstery", fallback: "Upholstery material" },
    { kind: "text", key: "upholstery_material_details", labelKey: "product.supp.upholstery-details", fallback: "Upholstery material details" },
    { kind: "bool", key: "headboard_included", labelKey: "product.supp.headboard-included", fallback: "Headboard included" },
    { kind: "bool", key: "bed_storage_included", labelKey: "product.supp.bed-storage", fallback: "Bed storage included" },
    { kind: "bool", key: "box_spring_included", labelKey: "product.supp.box-spring-included", fallback: "Box spring included" },
    { kind: "bool", key: "trundle_bed_included", labelKey: "product.supp.trundle", fallback: "Trundle bed included" },
    { kind: "int", key: "number_of_compartments", labelKey: "product.supp.compartments", fallback: "Compartments" },
    { kind: "int", key: "number_of_doors", labelKey: "product.supp.doors", fallback: "Doors" },
    { kind: "int", key: "number_of_drawers", labelKey: "product.supp.drawers", fallback: "Drawers" },
    { kind: "int", key: "number_of_shelves", labelKey: "product.supp.shelves", fallback: "Shelves" },
    { kind: "number", key: "weight_capacity", labelKey: "product.supp.weight-capacity", fallback: "Weight capacity (lb)" },
  ];

  return (
    <Card className="p-5">
      <SpecForm
        fields={fields}
        value={data as ProductSupplementaryDataDTO & Record<string, unknown>}
        onChange={(next) => onChange({ ...draft, supplementary_data: next })}
        readOnly={readOnly}
      />
    </Card>
  );
}

/** Compliance and certification claims — the feeds treat these as required. */
export function CertificationsTab({ draft, onChange, readOnly }: Omit<TabProps, "onPersisted">) {
  const data = draft.certification_data ?? {};

  const fields: Array<SpecField<ProductCertificationDataDTO>> = [
    { kind: "text", key: "warranty", labelKey: "product.cert.warranty", fallback: "Warranty" },
    { kind: "select", key: "supplier_intended_and_approved_use", labelKey: "product.cert.approved-use", fallback: "Intended and approved use", options: APPROVED_USES },
    { kind: "text", key: "cpsia_cautionary_statement", labelKey: "product.cert.cpsia", fallback: "CPSIA cautionary statement" },
    { kind: "bool", key: "carb2_compliance", labelKey: "product.cert.carb2", fallback: "CARB2 compliance" },
    { kind: "bool", key: "california_proposition_65_warning", labelKey: "product.cert.prop65", fallback: "California Prop. 65 warning" },
    { kind: "bool", key: "fsc_certification", labelKey: "product.cert.fsc", fallback: "FSC" },
    { kind: "bool", key: "gols3_certification", labelKey: "product.cert.gols3", fallback: "GOLS3" },
    { kind: "bool", key: "greenguard_certification", labelKey: "product.cert.greenguard", fallback: "Greenguard" },
    { kind: "bool", key: "greenguard_gold_certification", labelKey: "product.cert.greenguard-gold", fallback: "Greenguard Gold" },
    { kind: "bool", key: "grs_certification", labelKey: "product.cert.grs", fallback: "GRS" },
    { kind: "bool", key: "rcs_certification", labelKey: "product.cert.rcs", fallback: "RCS" },
    { kind: "bool", key: "iso_14021_recycled_content_standard_certification", labelKey: "product.cert.iso14021", fallback: "ISO 14021 recycled content" },
    { kind: "bool", key: "certipur_us_certification", labelKey: "product.cert.certipur", fallback: "CertiPUR-US" },
    { kind: "bool", key: "gots5_certification", labelKey: "product.cert.gots5", fallback: "GOTS5" },
  ];

  return (
    <Card className="p-5">
      <SpecForm
        fields={fields}
        value={data as ProductCertificationDataDTO & Record<string, unknown>}
        onChange={(next) => onChange({ ...draft, certification_data: next })}
        readOnly={readOnly}
      />
    </Card>
  );
}
